import { createContext, useContext, useEffect, useState } from "react";
import {
  GoogleAuthProvider,
  signInWithPopup,
  onAuthStateChanged,
} from "firebase/auth";
import { api } from "./api";
import { tracker } from "./tracker";
import { auth } from "./firebase";
import type { SupabaseUser, AccessLevel } from "./constants";
import { STORAGE_KEY, ACCESS_LEVEL_KEY, USER_ENTITIES_KEY, ROUTE_PERMISSIONS_KEY } from "./constants";
import { useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import type { UserRole } from "./permissions";
import { USER_ROLE_KEY } from "./constants";

export interface UserEntity {
  entity_id: number;
  name: string;
  branch_id?: number | null;
}

interface AuthContextType {
  user: SupabaseUser | null;
  accessLevel: AccessLevel | null;
  userRole: UserRole | null;
  userEntities: UserEntity[];
  routePermissions: Record<string, string[]>;
  excelPermissions: {
    canDownloadExcel: boolean;
    canUploadExcel: boolean;
  };
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshUserRole: () => Promise<void>;
  refreshUserEntities: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<SupabaseUser | null>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  });
  const [accessLevel, setAccessLevel] = useState<AccessLevel | null>(() => {
    const stored = localStorage.getItem(ACCESS_LEVEL_KEY);
    return stored ? JSON.parse(stored) : null;
  });
  const [userRole, setUserRole] = useState<UserRole | null>(() => {
    const stored = localStorage.getItem(USER_ROLE_KEY);
    return stored ? JSON.parse(stored) : null;
  });
  const [userEntities, setUserEntities] = useState<UserEntity[]>(() => {
    const stored = localStorage.getItem(USER_ENTITIES_KEY);
    return stored ? JSON.parse(stored) : [];
  });
  const [excelPermissions, setExcelPermissions] = useState<{
    canDownloadExcel: boolean;
    canUploadExcel: boolean;
  }>({ canDownloadExcel: false, canUploadExcel: false });
  const [routePermissions, setRoutePermissions] = useState<Record<string, string[]>>(() => {
    const stored = localStorage.getItem(ROUTE_PERMISSIONS_KEY);
    return stored ? JSON.parse(stored) : {};
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {

    // Firebase auth state listener (for Google users)
    const unsubscribe = onAuthStateChanged(auth, async firebaseUser => {
      const supabaseUser = localStorage.getItem(STORAGE_KEY);

      if (firebaseUser) {
        try {
          const supabaseUserStr = localStorage.getItem(STORAGE_KEY);
          if (!supabaseUserStr) {
            navigate("/login");
            setLoading(false);
            return;
          }

          const supabaseUser = JSON.parse(supabaseUserStr);
          setUser(supabaseUser);

          // Fetch access level, user role and entities if user exists
          if (supabaseUser?.id) {
            await Promise.all([
              fetchAccessLevel(supabaseUser.id),
              fetchUserRole(supabaseUser.id),
              fetchUserEntities(supabaseUser.id),
              fetchRoutePermissions(),
            ]);
          }
        } catch (error: any) {
          Sentry.captureException("Error syncing auth:", error.message);
          setUser(null);
          localStorage.removeItem(STORAGE_KEY);
          navigate("/login");
        }
      } else {
        setUser(null);
        setUserRole(null);
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(USER_ROLE_KEY);
        navigate("/login");
      }
      setLoading(false);
    });

    return () => {
      unsubscribe();
    };
  }, [navigate]);

  // Fetch user role when component mounts if user exists
  useEffect(() => {
    if (user?.id && !userRole) {
      console.log("Fetching user role on mount for user:", user.id);
      fetchUserRole(user.id);
    }
  }, [user?.id]);

  // Fetch user entities when component mounts if user exists
  useEffect(() => {
    if (user?.id && userEntities.length === 0) {
      fetchUserEntities(user.id);
    }
  }, [user?.id]);

  const fetchAccessLevel = async (userId: string) => {
    try {
      const response = await api.getAccessLevel(userId);
      if (response.success) {
        setAccessLevel(response.results);
        localStorage.setItem(
          ACCESS_LEVEL_KEY,
          JSON.stringify(response.results),
        );
      }
    } catch (error: any) {
      Sentry.captureException("Error fetching access level:", error.message);
    }
  };

  const fetchUserRole = async (userId: string) => {
    try {
      console.log("Fetching user role for userId:", userId);
      const response = await api.getUserRole(userId);
      console.log("User role response:", response);
      if (response?.success && response.data) {
        const roleData = {
          role_id: response.data.role_id,
          role_name: response.data.role_name,
          role_description: response.data.role_description,
          // Permissions now come from the backend RBAC join
          permissions: response.data.permissions || [],
          // Pre-computed list of exact routes this role can access (avoids slug-collision bugs)
          allowed_routes: response.data.allowed_routes || [],
        };
        console.log("Setting user role:", roleData);
        setUserRole(roleData);
        localStorage.setItem(USER_ROLE_KEY, JSON.stringify(roleData));

        // Set Excel permissions from role data
        setExcelPermissions({
          canDownloadExcel: response.data.can_download_excel ?? false,
          canUploadExcel: response.data.can_upload_excel ?? false,
        });
      } else {
        console.log("No role found for user");
        setUserRole(null);
        localStorage.removeItem(USER_ROLE_KEY);
      }
    } catch (error: any) {
      console.error("Error fetching user role:", error);
      setUserRole(null);
      localStorage.removeItem(USER_ROLE_KEY);
    }
  };

  const refreshUserRole = async () => {
    if (user?.id) {
      await fetchUserRole(user.id);
    }
  };

  const fetchUserEntities = async (userId: string) => {
    try {
      const response = await api.getUserEntities(userId);
      if (response?.success && Array.isArray(response.data)) {
        const mapped: UserEntity[] = response.data.map((e: any) => ({
          entity_id: e.entity_id,
          name: e.name,
          branch_id: e.branch_id ?? null,
        }));
        setUserEntities(mapped);
        localStorage.setItem(USER_ENTITIES_KEY, JSON.stringify(mapped));
      } else {
        setUserEntities([]);
        localStorage.removeItem(USER_ENTITIES_KEY);
      }
    } catch (error: any) {
      console.error("Error fetching user entities:", error);
      setUserEntities([]);
      localStorage.removeItem(USER_ENTITIES_KEY);
    }
  };

  const refreshUserEntities = async () => {
    if (user?.id) {
      await fetchUserEntities(user.id);
    }
  };

  const fetchRoutePermissions = async () => {
    try {
      const response = await api.getRoutePermissions();
      if (response?.success && response.data) {
        setRoutePermissions(response.data);
        localStorage.setItem(ROUTE_PERMISSIONS_KEY, JSON.stringify(response.data));
      }
    } catch (error: any) {
      console.error("Error fetching route permissions:", error);
    }
  };

  const signInWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope("email");
      provider.addScope("profile");

      const result = await signInWithPopup(auth, provider);
      const credential = GoogleAuthProvider.credentialFromResult(result);
      const firebaseUser = result.user;

      if (!credential || !firebaseUser.email) {
        Sentry.captureException("No credential or email returned");
        throw new Error("No credential or email returned");
      }

      const supabaseUser = await api.getUser(firebaseUser.email!);

      if (supabaseUser.results) {
        setUser(supabaseUser.results);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(supabaseUser.results));
        await Promise.all([
          fetchAccessLevel(supabaseUser.results.id),
          fetchUserRole(supabaseUser.results.id),
          fetchUserEntities(supabaseUser.results.id),
          fetchRoutePermissions(),
        ]);
        navigate("/");
        tracker.trackEvent("login_success", { method: "google" });
      } else {
        Sentry.captureException("No supabase user returned");
        navigate("/unauthorized");
        tracker.trackEvent("login_unauthorized", { method: "google" });
      }
    } catch (error: any) {
      Sentry.captureException("Error in sign in flow:", error.message);
      tracker.trackEvent("login_error", {
        error: error instanceof Error ? error.message : "Unknown error",
        method: "google",
      });
      navigate("/unauthorized");
    }
  };

  const signInWithEmail = async (email: string, password: string) => {
    try {
      const data = await api.loginWithEmail(email, password);

      if (!data || !data.success) {
        throw new Error(data?.message || "Invalid login credentials");
      }

      if (!data.results?.user?.email) {
        throw new Error("No user returned from sign in");
      }

      const supabaseUser = await api.getUser(data.results.user.email);

      if (supabaseUser.results) {
        setUser(supabaseUser.results);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(supabaseUser.results));
        await fetchAccessLevel(supabaseUser.results.id);
        navigate("/");
        tracker.trackEvent("login_success", { method: "email" });
      } else {
        Sentry.captureException("No supabase user returned for email login");
        navigate("/unauthorized");
        tracker.trackEvent("login_unauthorized", { method: "email" });
      }
    } catch (error: any) {
      Sentry.captureException("Error in email sign in flow:", error.message);
      tracker.trackEvent("login_error", {
        error: error instanceof Error ? error.message : "Unknown error",
        method: "email",
      });
      throw error;
    }
  };

  const signOut = async () => {
    try {
      auth.signOut();
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(ACCESS_LEVEL_KEY);
      localStorage.removeItem(USER_ROLE_KEY);
      localStorage.removeItem(USER_ENTITIES_KEY);
      localStorage.removeItem(ROUTE_PERMISSIONS_KEY);
      setUser(null);
      setAccessLevel(null);
      setUserRole(null);
      setUserEntities([]);
      setRoutePermissions({});
      navigate("/login");
    } catch (error: any) {
      Sentry.captureException("Error signing out:", error.message);
      throw error;
    }
  };

  const value = {
    user,
    accessLevel,
    userRole,
    userEntities,
    routePermissions,
    excelPermissions,
    loading,
    signInWithGoogle,
    signInWithEmail,
    signOut,
    refreshUserRole,
    refreshUserEntities,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
